import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useParams } from 'react-router';
import { toast } from 'sonner';
import { emit } from '@/lib/analytics';
import { useFetch } from '@/hooks/use-fetch';
import { useWorkspace } from '@/contexts/workspace-context';
import { isValidPublicId } from '@/lib/public-id';
import type { WorkspacePublicId } from '@hookmyapp/shared';
import { launchMetaSignup } from '@/lib/meta-signup';
import { launchInstagramSignup } from '@/lib/instagram-signup';
import { startFacebookConnect } from '@/lib/facebook-connect';
import { formatInstagramHandle } from '@/lib/channel-display';
import { captureException } from '@/lib/sentry';
import {
  useChannelDetail,
  useClearWebhookConfig,
  useRefreshChannel,
  useRotateHmac,
  useSetChannelForwarding,
  useSetMetaRetry,
} from '@/components/channels/queries';
import { ChannelCardShell } from '@/components/channels/channel-card-shell';
import { ChannelBodySkeleton } from '@/components/channels/channel-body-skeleton';
import { WhatsAppChannelBody } from '@/components/channels/whatsapp-channel-body';
import { InstagramChannelBody } from '@/components/channels/instagram-channel-body';
import { FacebookChannelBody } from '@/components/channels/facebook-channel-body';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { Channel } from '@/types/channel';

interface ChannelDetailViewProps {
  channel: Channel;
  userRole: 'admin' | 'member';
  onChannelUpdated: () => void;
  onDisconnected?: () => void;
}

export function ChannelDetailView({
  channel,
  userRole,
  onChannelUpdated,
  onDisconnected,
}: ChannelDetailViewProps) {
  const fetchWithAuth = useFetch();
  const { refreshUsage, activeWorkspace, sessionScoped } = useWorkspace();
  // Workspace-scoped actions target the URL's :ws, not the cached
  // activeWorkspace, which can lag behind the page (AIT-207 wrong-target bug).
  const { ws: routeWs } = useParams();
  const wsId =
    routeWs && isValidPublicId(routeWs, 'ws')
      ? (routeWs as WorkspacePublicId)
      : activeWorkspace?.id;

  // Lifted state — the body components read these via props; the shell owns
  // the action handlers that mutate them.
  const [currentWebhookUrl, setCurrentWebhookUrl] = useState(channel.webhookUrl);
  const [currentVerifyToken, setCurrentVerifyToken] = useState(channel.verifyToken);
  const [currentHmacSecret, setCurrentHmacSecret] = useState(channel.hmacSecret);
  // WA-only state — initialised from the channel when it's a WhatsApp channel,
  // null otherwise. The WhatsApp case branch below is the only branch that
  // passes or uses these values.
  const [currentWabaName, setCurrentWabaName] = useState<string | null>(
    channel.type === 'whatsapp' ? channel.whatsappWabaName : null,
  );
  const [currentBusinessName, setCurrentBusinessName] = useState<string | null>(null);
  const [currentQualityRating, setCurrentQualityRating] = useState<string | null>(
    channel.type === 'whatsapp' ? channel.whatsappQualityRating : null,
  );
  const [copiedCredentials, setCopiedCredentials] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showUrlDialog, setShowUrlDialog] = useState(false);
  const [urlDialogMode, setUrlDialogMode] = useState<'configure' | 'edit'>('configure');
  const [rotateHmacDialogOpen, setRotateHmacDialogOpen] = useState(false);
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [switchToCliDialogOpen, setSwitchToCliDialogOpen] = useState(false);
  const [currentForwardingEnabled, setCurrentForwardingEnabled] = useState(channel.forwardingEnabled);
  const [currentMetaRetryDisabled, setCurrentMetaRetryDisabled] = useState(channel.metaRetryDisabled);
  const [retryDialogOpen, setRetryDialogOpen] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [consecutiveForwardFailures, setConsecutiveForwardFailures] = useState(0);
  const [currentPhoneNumberId, setCurrentPhoneNumberId] = useState<string | null>(null);
  const [reconnecting, setReconnecting] = useState(false);

  // Query/mutation plumbing. The detail GET is a cached read; the refresh POST
  // is a mutation whose hook-level onSuccess invalidates the detail key.
  // Separate refresh instances so the "Refresh" and quality spinners stay
  // independent (and the on-mount refresh drives only the skeleton).
  const detailQuery = useChannelDetail(wsId, channel.id, sessionScoped);
  const mountRefresh = useRefreshChannel(wsId);
  const refreshAll = useRefreshChannel(wsId);
  const refreshQuality = useRefreshChannel(wsId);
  const setForwarding = useSetChannelForwarding();
  const setMetaRetry = useSetMetaRetry();
  const clearWebhook = useClearWebhookConfig();
  const rotateHmac = useRotateHmac();

  const refreshingQuality = refreshQuality.isPending;
  const refreshingAll = refreshAll.isPending;
  const rotatingHmac = rotateHmac.isPending;
  const switchingToCli = clearWebhook.isPending;
  const toggling = setForwarding.isPending;
  const togglingRetry = setMetaRetry.isPending;

  // Reconnect-in-place (D3/D4). WhatsApp re-launches the embedded signup with
  // this channel's id + create-new-allowed mode so the backend authorizes +
  // mints against the existing channel row. Instagram routes to the existing
  // IG connect flow (resurrect-or-create) — not regressed by this phase.
  async function handleReconnect() {
    if (!wsId) return;
    setReconnecting(true);
    try {
      if (channel.type === 'whatsapp') {
        await launchMetaSignup(wsId, fetchWithAuth, { channelId: channel.id });
      } else if (channel.type === 'instagram' && channel.connectionType === 'facebook_login' && channel.metaPageId) {
        // A linked account reconnects through its Page (Codex round 25):
        // Instagram Login would re-key the row to the other flow and drop the
        // credential it shares with the Page.
        await startFacebookConnect(wsId, fetchWithAuth, { reconnectPageId: channel.metaPageId });
      } else if (channel.type === 'instagram') {
        await launchInstagramSignup(wsId, fetchWithAuth, { reconsent: true });
      } else {
        // Facebook (AIT-621): the connect flow reconnects a disconnected Page
        // in place, pinned to THIS Page; the relay lands in the Connect
        // Channel modal, which picks the Page without offering others.
        await startFacebookConnect(wsId, fetchWithAuth, { reconnectPageId: channel.metaResourceId });
      }
    } catch (e) {
      captureException(e);
      toast.error("We couldn't start the reconnect right now. Please try again in a moment.");
    } finally {
      setReconnecting(false);
    }
  }

  // Fetches the canonical env payload from the backend's
  // /meta/channels/:id/env endpoint. The endpoint is the single source of
  // truth for the .env shape (Meta Graph version, decrypted token, etc.) —
  // duplicating these values in the React handlers (as we used to) silently
  // drifts every time the backend's contract changes. Returns null + surfaces
  // a toast on any error; caller decides whether to short-circuit.
  // ponytail: stays a direct on-demand fetch — it's fired from click handlers,
  // and caching a decrypted-credentials payload in the Query cache buys nothing.
  async function fetchEnvPayload(): Promise<Record<string, string> | null> {
    try {
      const res = await fetchWithAuth(`/meta/channels/${channel.id}/env`);
      if (!res.ok) return null;
      const data = (await res.json()) as { values?: Record<string, string> };
      return data.values ?? null;
    } catch {
      return null;
    }
  }

  async function handleCopyCredentials() {
    const values = await fetchEnvPayload();
    if (!values) {
      toast.error('Failed to copy. Please copy manually.');
      return;
    }
    const lines = Object.entries(values)
      .map(([k, v]) => `${k}=${v}`)
      .join('\n');
    try {
      await navigator.clipboard.writeText(lines);
      emit('credentials_copied', { channel_id: channel.id, waba_name: currentWabaName });
      setCopiedCredentials(true);
      toast.success('Credentials copied to clipboard');
      setTimeout(() => setCopiedCredentials(false), 2000);
    } catch {
      toast.error('Failed to copy. Please copy manually.');
    }
  }

  async function handleDownload() {
    setDownloading(true);
    try {
      const values = await fetchEnvPayload();
      if (!values) {
        toast.error('Failed to download. Please try again.');
        return;
      }

      const envBlock = Object.entries(values)
        .map(([k, v]) => `${k}=${v}`)
        .join('\n');
      const headerLabel =
        channel.type === 'instagram' ? 'Instagram' : channel.type === 'facebook' ? 'Facebook' : 'WhatsApp';
      const fallbackName = (() => {
        if (channel.type === 'instagram') return channel.instagramUsername || 'channel';
        if (channel.type === 'facebook') return channel.facebookPageName || 'channel';
        return currentWabaName || 'channel';
      })();
      const content = [
        `# HookMyApp - ${headerLabel} Credentials`,
        `# Channel: ${fallbackName}`,
        `# Downloaded: ${new Date().toISOString().split('T')[0]}`,
        '',
        envBlock,
        '',
      ].join('\n');

      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const safeName = fallbackName.replace(/[^a-zA-Z0-9-_]/g, '-').toLowerCase();
      a.href = url;
      a.download = `hookmyapp-${safeName}-credentials.txt`;
      emit('credentials_downloaded', { channel_id: channel.id, waba_name: currentWabaName });
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download. Please try again.');
    } finally {
      setDownloading(false);
    }
  }

  function handleRefreshQuality() {
    refreshQuality.mutate(
      { channelId: channel.id },
      {
        onSuccess: (data) => {
          if (data.whatsappQualityRating !== undefined) setCurrentQualityRating(data.whatsappQualityRating);
          onChannelUpdated();
          toast.success('Quality rating refreshed');
        },
        onError: () => toast.error('Failed to refresh quality rating'),
      },
    );
  }

  function handleRefreshAll() {
    // refresh refetches everything: names, quality, subscription, connection type
    refreshAll.mutate(
      { channelId: channel.id },
      {
        onSuccess: async (data) => {
          if (data.deleted) {
            onDisconnected?.();
            return;
          }
          if (data.whatsappWabaName !== undefined) setCurrentWabaName(data.whatsappWabaName);
          if (data.whatsappBusinessName !== undefined) setCurrentBusinessName(data.whatsappBusinessName);
          if (data.whatsappQualityRating !== undefined) setCurrentQualityRating(data.whatsappQualityRating);
          if (data.consecutiveForwardFailures !== undefined) setConsecutiveForwardFailures(data.consecutiveForwardFailures);
          onChannelUpdated();
          // Also refetch usage so "Billable Messages" (here + sidebar + billing,
          // all reading the shared workspace-context usage) reflects messages that
          // arrived since page load — no browser hard-refresh required.
          await refreshUsage();
          toast.success('Channel refreshed');
        },
        onError: () => toast.error('Failed to refresh channel'),
      },
    );
  }

  function handleEnable() {
    setForwarding.mutate(
      { channelId: channel.id, enabled: true },
      {
        onSuccess: () => {
          emit('forwarding_enabled', { channel_id: channel.id, waba_name: currentWabaName });
          setCurrentForwardingEnabled(true);
          toast.success('Messaging enabled');
          onChannelUpdated();
        },
        onError: () => toast.error('Failed to enable messaging. Please try again.'),
      },
    );
  }

  function handleDisable() {
    setForwarding.mutate(
      { channelId: channel.id, enabled: false },
      {
        onSuccess: () => {
          emit('forwarding_disabled', { channel_id: channel.id, waba_name: currentWabaName });
          setCurrentForwardingEnabled(false);
          toast.success('Messaging disabled');
          setDisableDialogOpen(false);
          onChannelUpdated();
        },
        onError: () => toast.error('Failed to disable messaging. Please try again.'),
      },
    );
  }

  // Meta-retry toggle. The "Retry" switch is ON when retries are enabled
  // (metaRetryDisabled === false). Backend endpoint takes { enabled } and
  // returns the resolved { metaRetryDisabled }. Turning ON is immediate;
  // turning OFF is gated behind DisableRetryDialog (parent opens it).
  function handleEnableRetry() {
    setMetaRetry.mutate(
      { channelId: channel.id, enabled: true },
      {
        onSuccess: () => {
          emit('meta_retry_enabled', { channel_id: channel.id, waba_name: currentWabaName });
          setCurrentMetaRetryDisabled(false);
          toast.success('Retries enabled');
          onChannelUpdated();
        },
        onError: () => toast.error('Failed to enable retries. Please try again.'),
      },
    );
  }

  function handleDisableRetry() {
    setMetaRetry.mutate(
      { channelId: channel.id, enabled: false },
      {
        onSuccess: () => {
          emit('meta_retry_disabled', { channel_id: channel.id, waba_name: currentWabaName });
          setCurrentMetaRetryDisabled(true);
          toast.success('Retries turned off');
          setRetryDialogOpen(false);
          onChannelUpdated();
        },
        onError: () => toast.error('Failed to turn off retries. Please try again.'),
      },
    );
  }

  function handleSwitchToCli() {
    clearWebhook.mutate(
      { channelId: channel.id },
      {
        onSuccess: () => {
          emit('webhook_cleared', {
            channel_id: channel.id,
            waba_name: currentWabaName,
          });
          setCurrentWebhookUrl(null);
          setSwitchToCliDialogOpen(false);
          toast.success('Switched to HookMyApp CLI');
          onChannelUpdated();
        },
        onError: () => toast.error('Failed to switch. Please try again.'),
      },
    );
  }

  // Rotate the per-channel HMAC signing secret (SEPARATE from the verify token,
  // which is never touched here). Breaks the customer's stored signing secret
  // until they copy the new one in, so it sits behind a confirm dialog.
  function handleRotateHmac() {
    rotateHmac.mutate(
      { channelId: channel.id },
      {
        onSuccess: (data) => {
          setCurrentHmacSecret(data.hmacSecret);
          setRotateHmacDialogOpen(false);
          toast.success('HMAC secret rotated. Update it in your endpoint.');
          // No onChannelUpdated(): the secret isn't shown in the channels list, and
          // refreshing would remount this view and reset the field to the stale prop.
        },
        onError: () => toast.error('Failed to rotate. Please try again.'),
      },
    );
  }

  function handleUrlConfigured(webhookUrl: string, verifyToken: string) {
    emit('webhook_configured', { channel_id: channel.id, waba_name: currentWabaName, mode: urlDialogMode });
    setShowUrlDialog(false);
    setCurrentWebhookUrl(webhookUrl);
    setCurrentVerifyToken(verifyToken);
    onChannelUpdated();
  }

  // Refresh channel from Meta on mount — detects auto-deletion or updates.
  // The Task 4 backend dispatcher routes /refresh to the correct IG vs WA
  // service based on channel.type. The refreshRan ref (not an effect-cleanup
  // flag) is what prevents duplicate execution under StrictMode's replay.
  const refreshRan = useRef(false);
  const [mountRefreshSettled, setMountRefreshSettled] = useState(false);
  useEffect(() => {
    if (refreshRan.current) return;
    refreshRan.current = true;
    // mutateAsync (NOT mutate-with-callbacks): per-mutate callbacks are
    // observer-bound and get dropped by StrictMode's simulated unmount, while
    // the refreshRan ref stops the effect replay from re-arming them — the
    // promise chain below survives like the old raw-fetch version did.
    mountRefresh
      .mutateAsync({ channelId: channel.id })
      .then((data) => {
        if (data.deleted) {
          // Channel was auto-deleted by backend — redirect to channels list
          onDisconnected?.();
          return;
        }
        if (data.whatsappQualityRating !== undefined) setCurrentQualityRating(data.whatsappQualityRating);
        if (data.consecutiveForwardFailures !== undefined) setConsecutiveForwardFailures(data.consecutiveForwardFailures);
        if (data.whatsappBusinessName !== undefined) setCurrentBusinessName(data.whatsappBusinessName);
        // Don't call onChannelUpdated() here — it triggers refreshChannels()
        // which replaces the channels array, causing this component to remount
        // and fire the refresh again in an infinite loop.
      })
      .catch(() => {
        // Silent — the on-mount refresh is best-effort.
      })
      .finally(() => setMountRefreshSettled(true));
  }, [channel.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Detail data (whatsappPhoneNumberId, whatsappBusinessName) syncs into lifted
  // state: the refresh mutations above also write currentBusinessName, so the
  // fields stay useState rather than being derived from a single source.
  useEffect(() => {
    const detail = detailQuery.data;
    if (!detail) return;
    if (detail.whatsappPhoneNumberId !== undefined) setCurrentPhoneNumberId(detail.whatsappPhoneNumberId);
    if (detail.whatsappBusinessName !== undefined) setCurrentBusinessName(detail.whatsappBusinessName);
  }, [detailQuery.data]);

  // True until the on-mount detail + refresh settle. Gates the body skeleton
  // so async fields (Meta Business Portfolio, Phone Number ID, quality)
  // resolve before render instead of flashing "N/A". When the query cache
  // already held detail data AT MOUNT (remount after disable/enable toggles),
  // render it immediately instead of skeleton-flashing the whole card while
  // the background refresh runs (AIT-85). Checking data-at-mount — not data
  // now — keeps the skeleton up through the refresh window on fresh loads.
  const [hadCacheAtMount] = useState(() => detailQuery.data !== undefined);
  const detailLoading = detailQuery.isPending || (!mountRefreshSettled && !hadCacheAtMount);

  // Per-type dispatch — pick body + title + test-CTA shape from channel.type.
  let body: ReactNode;
  let title: string;
  let testActionLabel: string | undefined;
  let testActionHref: string | null = null;
  switch (channel.type) {
    case 'whatsapp':
      body = (
        <WhatsAppChannelBody
          channel={channel}
          canEdit={userRole === 'admin'}
          currentWebhookUrl={currentWebhookUrl}
          currentVerifyToken={currentVerifyToken}
          setShowUrlDialog={setShowUrlDialog}
          setUrlDialogMode={setUrlDialogMode}
          setSwitchToCliDialogOpen={setSwitchToCliDialogOpen}
          currentHmacSecret={currentHmacSecret}
          onRotateHmac={() => setRotateHmacDialogOpen(true)}
          rotatingHmac={rotatingHmac}
          currentWabaName={currentWabaName}
          currentBusinessName={currentBusinessName}
          currentPhoneNumberId={currentPhoneNumberId}
          onProfilePhotoChanged={() => refreshAll.mutate({ channelId: channel.id }, { onSuccess: () => onChannelUpdated() })}
        />
      );
      title = currentWabaName || 'WhatsApp Business Account';
      testActionLabel = 'Send Test Message';
      testActionHref = channel.whatsappDisplayPhoneNumber
        ? `https://wa.me/${channel.whatsappDisplayPhoneNumber.replace(/[^0-9]/g, '')}`
        : null;
      break;
    case 'instagram':
      body = (
        <InstagramChannelBody
          channel={channel}
          canEdit={userRole === 'admin'}
          currentWebhookUrl={currentWebhookUrl}
          currentVerifyToken={currentVerifyToken}
          setShowUrlDialog={setShowUrlDialog}
          setUrlDialogMode={setUrlDialogMode}
          setSwitchToCliDialogOpen={setSwitchToCliDialogOpen}
          currentHmacSecret={currentHmacSecret}
          onRotateHmac={() => setRotateHmacDialogOpen(true)}
          rotatingHmac={rotatingHmac}
        />
      );
      title =
        channel.instagramProfileName?.trim() && channel.instagramProfileName !== channel.instagramUsername
          ? channel.instagramProfileName
          : channel.instagramUsername
            ? formatInstagramHandle(channel.instagramUsername)
            : 'Instagram Account';
      testActionLabel = 'Send Test DM';
      testActionHref = channel.instagramUsername
        ? `https://ig.me/m/${channel.instagramUsername}`
        : null;
      break;
    case 'facebook':
      body = (
        <FacebookChannelBody
          channel={channel}
          canEdit={userRole === 'admin'}
          currentWebhookUrl={currentWebhookUrl}
          currentVerifyToken={currentVerifyToken}
          setShowUrlDialog={setShowUrlDialog}
          setUrlDialogMode={setUrlDialogMode}
          setSwitchToCliDialogOpen={setSwitchToCliDialogOpen}
          currentHmacSecret={currentHmacSecret}
          onRotateHmac={() => setRotateHmacDialogOpen(true)}
          rotatingHmac={rotatingHmac}
        />
      );
      title = channel.facebookPageName?.trim() || 'Facebook Page';
      testActionLabel = 'Send Test Message';
      testActionHref = `https://m.me/${channel.metaResourceId}`;
      break;
    default:
      // Defensive — a channel.type without a UI dispatcher branch should
      // never reach this view (backend's getChannelDetail filters), so
      // rendering nothing here is correct fallback, not a swallowed bug.
      return null;
  }

  // While the on-mount fetch is in flight, replace the per-type body with the
  // shared skeleton — uniform for WhatsApp and Instagram, no N/A flash, no
  // layout shift when the async rows arrive.
  if (detailLoading) {
    body = <ChannelBodySkeleton />;
  }

  return (
    <>
    <ChannelCardShell
      channel={channel}
      title={title}
      body={body}
      testActionLabel={testActionLabel}
      testActionHref={testActionHref}
      userRole={userRole}
      onDisconnected={onDisconnected}
      onReconnect={handleReconnect}
      reconnecting={reconnecting}
      currentForwardingEnabled={currentForwardingEnabled}
      currentWebhookUrl={currentWebhookUrl}
      currentVerifyToken={currentVerifyToken}
      currentQualityRating={currentQualityRating}
      consecutiveForwardFailures={consecutiveForwardFailures}
      onRefreshAll={handleRefreshAll}
      refreshingAll={refreshingAll}
      onDownload={handleDownload}
      downloading={downloading}
      onCopyCredentials={handleCopyCredentials}
      copiedCredentials={copiedCredentials}
      onEnable={handleEnable}
      onDisable={handleDisable}
      toggling={toggling}
      currentMetaRetryDisabled={currentMetaRetryDisabled}
      onEnableRetry={handleEnableRetry}
      onDisableRetry={handleDisableRetry}
      togglingRetry={togglingRetry}
      retryDialogOpen={retryDialogOpen}
      setRetryDialogOpen={setRetryDialogOpen}
      onRefreshQuality={handleRefreshQuality}
      refreshingQuality={refreshingQuality}
      showUrlDialog={showUrlDialog}
      setShowUrlDialog={setShowUrlDialog}
      urlDialogMode={urlDialogMode}
      setUrlDialogMode={setUrlDialogMode}
      onUrlConfigured={handleUrlConfigured}
      disableDialogOpen={disableDialogOpen}
      setDisableDialogOpen={setDisableDialogOpen}
      switchToCliDialogOpen={switchToCliDialogOpen}
      setSwitchToCliDialogOpen={setSwitchToCliDialogOpen}
      onSwitchToCli={handleSwitchToCli}
      switchingToCli={switchingToCli}
      showVideoModal={showVideoModal}
      setShowVideoModal={setShowVideoModal}
    />

    <AlertDialog
      open={rotateHmacDialogOpen}
      onOpenChange={(open) => {
        if (!rotatingHmac && !open) setRotateHmacDialogOpen(false);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Rotate HMAC secret?</AlertDialogTitle>
          <AlertDialogDescription>
            A new HMAC signing secret is generated for this channel. Forwarded
            webhooks are signed with it right away, so your endpoint's signature
            check will fail until you copy the new secret in. The verify token is
            not affected, and only this channel is affected.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={rotatingHmac}>Keep Current</AlertDialogCancel>
          <AlertDialogAction
            disabled={rotatingHmac}
            onClick={(e) => {
              e.preventDefault();
              handleRotateHmac();
            }}
          >
            Rotate Secret
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
